import React, { useState, useEffect, PropsWithChildren, useReducer } from 'react'
import { useAccountSecurityContext } from 'providers'
import MFA from 'mfa-widget'
import { ErrorDialog } from 'functional-foundations/dialogs'

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '0.0.0.0'

interface MFAWrapperProps {
    url?: string // stage is default
    mode?: 'modal' | 'window' | 'anchor' // modal is default, for anchor keep the anchor div in the page & set the anchor id to mfaAnchor
    onClick?: () => void
    onSessionOpened?: () => void
    onFactorCompleted?: () => void
    onSessionCompleted: () => void
    onSessionFailed: () => void
    onChangeRequested?: () => void
    onClosed?: () => void
}

const sessionKeyDefaultData = { mfa_session_key: isLocalhost ? 'MQsYpGLQ-GLKmH3Su6SPz' : null }

const sessionKeyReducer = (state, action) => {
    switch (action.type) {
            case 'SET_DATA':
                return { ...state, data: action.payload }
            case 'SET_LOADING':
                if (action.payload === true) return { ...state, isLoading: true, data: sessionKeyDefaultData, error: '' }
                return { ...state, isLoading: false }
            case 'SET_ERROR':
                return { ...state, error: action.payload }
            case 'RESET':
                return { data: sessionKeyDefaultData, isLoading: false, error: '' }
            default:
                return state
    }
}

/**
 *
 */
const MFAWrapper = ({ url, mode, onClick, onSessionOpened, onFactorCompleted, onSessionCompleted, onSessionFailed, onChangeRequested, onClosed, children }: PropsWithChildren<MFAWrapperProps>) => {
    const [mfaCompleted, setMfaCompleted] = useState(false) // eslint-disable-line no-unused-vars
    const [sessionKey, dispatchSessionKey] = useReducer(sessionKeyReducer, { data: sessionKeyDefaultData, isLoading: false, error: '' })
    const [mfa] = useState(new MFA())
    const { client } = useAccountSecurityContext()

    const getMFAConfig = () => {
        let stage = (process.env.REACT_APP_STAGE as string)?.toLowerCase() || 'stage'

        let origin
        if (url) {
            stage = url
            if (url.startsWith('http')) origin = url
        }

        let mfaMode
        if (mode === 'window') mfaMode = mode

        const config: { stage: string, MFAOrigin?: string, mode?: string, closeable?: boolean, minHeight?: number } = { stage, MFAOrigin: origin }
        if (mfaMode) config.mode = mfaMode
        config.closeable = true

        return config
    }

    const getSessionKey = async () => {
        let sessionKey = ''
        dispatchSessionKey({ type: 'SET_LOADING', payload: true })
        try {
            await client.enableMFA({ requestNewSession: true })
        } catch (error: any) {
            const errorParsed = JSON.parse(error.error)
            if (error.status === 428) {
                sessionKey = errorParsed.details?.mfa.mfa_session_key
                dispatchSessionKey({ type: 'SET_DATA', payload: errorParsed.details?.mfa })
            } else {
                dispatchSessionKey({ type: 'SET_ERROR', payload: errorParsed.details?.message })
            }
        } finally {
            dispatchSessionKey({ type: 'SET_LOADING', payload: false })
        }
        return sessionKey
    }

    useEffect(() => {
        (async () => {
            const MFAEvents = {
                MFASessionOpened: () => onSessionOpened?.(),
                MFAFactorCompleted: () => onFactorCompleted?.(),
                MFASessionCompleted: () => { setMfaCompleted(true); onSessionCompleted() },
                MFASessionFailed: onSessionFailed,
                MFAChangeRequested: () => onChangeRequested?.(),
                MFAClosed: () => onClosed?.()
            }

            const config = getMFAConfig()

            mfa.init(MFAEvents, config)
        })()

        return () => {
            mfa.deactivate()
        }
    }, [])

    const openMFA = async (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()

        if (sessionKey.isLoading || !client._sessionToken) return
        const gotSessionKey = await getSessionKey()
        if (!gotSessionKey) return

        mfa.open(gotSessionKey)
        setMfaCompleted(false)
        onClick?.()
    }

    const childrenProps = sessionKey.isLoading || !client._sessionToken ? { loading: true } : {}

    return (
        <>
            <div onClick={(e) => openMFA(e)} style={{ cursor: 'pointer' }} data-testid='mfa-wrapper'>
                {React.cloneElement(children as JSX.Element, childrenProps)}
            </div>
            {sessionKey.error && <ErrorDialog isOpen={true} onClose={() => dispatchSessionKey({ type: 'RESET' })} title='There was issues processing your request.' subtitle={sessionKey.error} onConfirm={() => dispatchSessionKey({ type: 'RESET' })} />}
        </>
    )
}

MFAWrapper.displayName = 'MFAWrapper'

export default MFAWrapper
